/**
 * App lock (S03). While locked, the PWA covers all content until a user-verified passkey assertion
 * succeeds. The lock is a UX/shoulder-surfing control for an unattended unlocked device, not a
 * cryptographic boundary: keys stay non-extractable in IndexedDB either way (docs/e2e.md threat
 * model). The locked flag is persisted in IndexedDB so a reload stays locked, and the service worker
 * reads it (`app-lock-state.ts`) to keep notifications generic while locked.
 *
 * Policy (docs/security-multitenant.md F-12): Viko follows the device instead of in-tab activity,
 * which ignored work in other apps. Desktop locks when the OS screen locks, the system (not the tab)
 * is idle for 30 min, or the machine slept, using the Idle Detection API and/or the local vikod's
 * encrypted presence report. Without either signal: in-app idle (default 8 h) or hidden > 1 h.
 * Mobile locks after being in the background longer than a set time (default 4 h, passkey-budget
 * WP2: the phone's own lock screen covers short gaps; the return prompts Face ID by itself).
 * Signals can only lock. Unlocking takes the passkey ceremony, or (passkey-budget WP1, lock
 * follows the OS) a daemon-signed proof from this computer's vikod that the OS screen locked at or
 * after `presenceSince()` and was then unlocked by the user (`unlockWith`).
 */
import { randomBytes } from '@viko/crypto'
import { SYSTEM_IDLE_LOCK_MS } from '@viko/protocol'
import { APP_LOCK_KEY } from './app-lock-state.js'
import { loadContentTrust } from './content-trust.js'
import { idb } from './idb.js'
import type { PasskeyProvider } from './passkeys.js'

export { APP_LOCK_KEY } from './app-lock-state.js'
export const LOCK_POLICY_KEY = 'app-lock/policy/v1'
export interface AppLockRecord {
  locked: boolean
  lockedAt?: number
  /** Set while the page is hidden (or closed while hidden): the background/hidden clock. */
  hiddenAt?: number
  /** passkey-budget WP1: the OS screen lock that locked Viko (from vikod's presence report). */
  osLockedAt?: number
}

/**
 * passkey-budget WP1: when Viko locked for another reason (idle timer, sleep, reload), an OS lock
 * counts only if it is at most this much older than Viko's own lock (sleep reports lag a little).
 */
export const PRESENCE_LOCK_GRACE_MS = 2 * 60_000

export interface LockPolicy {
  /** `device`: follow OS lock / system idle (fallback timers without a signal). `idle`: in-app timer. */
  desktop: 'device' | 'idle'
  /** In-app idle timeout: the `idle` policy, and the `device` fallback without a signal. */
  idleMs: number
  /** Mobile: lock after being in the background this long. */
  backgroundMs: number
}
const MIN = 60_000
export const DEFAULT_LOCK_POLICY: LockPolicy = {
  desktop: 'device',
  idleMs: 8 * 60 * MIN,
  // passkey-budget WP2 (user decision): 4 h, not 30 min; Face ID auto-prompts on return.
  backgroundMs: 4 * 60 * MIN,
}
/** Desktop without a device signal: a page hidden this long locks. */
export const HIDDEN_FALLBACK_MS = 60 * MIN
export const IDLE_CHOICES_MS = [15 * MIN, 60 * MIN, 4 * 60 * MIN, 8 * 60 * MIN, 24 * 60 * MIN]
export const BACKGROUND_CHOICES_MS = [5 * MIN, 30 * MIN, 60 * MIN, 4 * 60 * MIN]

export type LockSignalSource = 'idle-detector' | 'daemon'
export interface LockSignal {
  /** The source is running and trustworthy enough to replace the fallback timers. */
  available: boolean
  screen?: 'locked' | 'unlocked' | 'unknown'
  /** When the OS screen last locked (the daemon keeps it after unlock, so a missed lock counts). */
  lockedAt?: number
  /** When system-wide idle last reached the threshold. */
  idleAt?: number
}
/** What currently decides when Viko locks (shown in You › Security). */
export type LockSource = LockSignalSource | 'fallback' | 'idle' | 'background'

const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
const TICK_MS = 15_000

export class IdleLock {
  private last: number
  /** Signals about events at or before this time were already handled (unlock / page start). */
  private baseline: number
  private hiddenAt: number | undefined
  private lastTick: number | undefined
  private isLocked = false
  private lockedAt: number | undefined
  private osLockedAt: number | undefined
  private policy: LockPolicy
  private readonly signals = new Map<LockSignalSource, boolean>()
  private readonly listeners = new Set<(locked: boolean) => void>()
  constructor(
    private readonly o: {
      now?: () => number
      /** Throws unless the user passes a verified passkey ceremony. */
      verify: () => Promise<void>
      persist?: (record: AppLockRecord) => Promise<void>
      policy?: LockPolicy
      /** Phone/tablet PWA: background timer instead of desktop signals. */
      mobile?: boolean
    },
  ) {
    this.last = this.baseline = this.now()
    this.policy = o.policy ?? DEFAULT_LOCK_POLICY
  }
  private now() {
    return (this.o.now ?? Date.now)()
  }
  get locked() {
    return this.isLocked
  }
  get mobile() {
    return !!this.o.mobile
  }
  subscribe(fn: (locked: boolean) => void) {
    this.listeners.add(fn)
    return () => void this.listeners.delete(fn)
  }
  private save(record: AppLockRecord) {
    void this.o.persist?.(record).catch(() => {})
  }
  private set(locked: boolean, at?: { lockedAt?: number; osLockedAt?: number }) {
    if (this.isLocked === locked) return
    this.isLocked = locked
    this.lockedAt = locked ? (at?.lockedAt ?? this.now()) : undefined
    this.osLockedAt = locked ? at?.osLockedAt : undefined
    this.save(
      locked
        ? {
            locked,
            lockedAt: this.lockedAt as number,
            ...(this.osLockedAt !== undefined ? { osLockedAt: this.osLockedAt } : {}),
          }
        : { locked },
    )
    for (const fn of this.listeners) fn(locked)
  }
  /**
   * passkey-budget WP1: the earliest OS screen lock that proves the user re-authenticated to the
   * OS after Viko locked: the OS lock that locked Viko, else Viko's own lock time (less a grace).
   */
  presenceSince(): number | undefined {
    if (!this.isLocked) return undefined
    if (this.osLockedAt !== undefined) return this.osLockedAt
    return (this.lockedAt ?? this.now()) - PRESENCE_LOCK_GRACE_MS
  }
  setPolicy(policy: LockPolicy) {
    this.policy = policy
    this.check()
  }
  /** The live signal (if any) that replaces the fallback timers. */
  source(): LockSource {
    if (this.o.mobile) return 'background'
    if (this.policy.desktop === 'idle') return 'idle'
    if (this.signals.get('daemon')) return 'daemon'
    if (this.signals.get('idle-detector')) return 'idle-detector'
    return 'fallback'
  }
  /** User activity while unlocked extends the session; activity while locked never unlocks. */
  activity() {
    if (!this.isLocked) this.last = this.now()
  }
  /** A device signal. It may lock (events newer than the last unlock); it never unlocks. */
  signal(source: LockSignalSource, s: LockSignal) {
    this.signals.set(source, s.available && s.screen !== 'unknown')
    if (this.o.mobile || this.isLocked) return
    const after = (t?: number) => t !== undefined && t > this.baseline
    // Timestamps, not the current state: a stale "locked" read after a passkey unlock never relocks.
    if (after(s.lockedAt)) this.set(true, { osLockedAt: s.lockedAt as number })
    else if (after(s.idleAt)) this.lock()
  }
  visibility(hidden: boolean) {
    if (hidden) {
      this.hiddenAt ??= this.now()
      if (!this.isLocked) this.save({ locked: false, hiddenAt: this.hiddenAt })
      return
    }
    this.check()
    this.hiddenAt = undefined
    if (!this.isLocked) this.save({ locked: false })
  }
  check() {
    if (this.isLocked) return
    const t = this.now()
    const hidden = this.hiddenAt === undefined ? 0 : t - this.hiddenAt
    const p = this.policy
    const source = this.source()
    if (source === 'background') {
      if (hidden >= p.backgroundMs) this.lock()
    } else if (source === 'idle') {
      if (t - this.last >= p.idleMs) this.lock()
    } else if (source === 'fallback') {
      if (t - this.last >= p.idleMs || hidden >= HIDDEN_FALLBACK_MS) this.lock()
    }
  }
  /** Periodic check; on desktop a wall-clock jump between ticks means the machine slept. */
  tick() {
    const t = this.now()
    const gap = this.lastTick === undefined ? 0 : t - this.lastTick
    this.lastTick = t
    if (!this.o.mobile && gap >= SYSTEM_IDLE_LOCK_MS) this.lock()
    this.check()
  }
  lock() {
    this.set(true)
  }
  /** Restore a persisted lock (reload/new tab) before any content renders. */
  restore(record: AppLockRecord | undefined) {
    if (record?.locked)
      return this.set(true, {
        ...(record.lockedAt !== undefined ? { lockedAt: record.lockedAt } : {}),
        ...(record.osLockedAt !== undefined ? { osLockedAt: record.osLockedAt } : {}),
      })
    if (record?.hiddenAt === undefined) return
    // Closed while hidden: that time counted as hidden, and signals since then still count.
    const hidden = this.now() - record.hiddenAt
    this.baseline = Math.min(this.baseline, record.hiddenAt)
    if (hidden >= (this.o.mobile ? this.policy.backgroundMs : HIDDEN_FALLBACK_MS)) this.set(true)
  }
  async unlock() {
    await this.o.verify()
    this.unlocked()
  }
  /**
   * passkey-budget WP1: unlock without a passkey when `check(presenceSince())` verifies a
   * daemon-signed OS lock-then-unlock proof. Resolves false (still locked) otherwise.
   */
  async unlockWith(check: (since: number) => Promise<boolean>): Promise<boolean> {
    const since = this.presenceSince()
    if (since === undefined) return false
    if (!(await check(since).catch(() => false))) return false
    // Locked again (or for another reason) while the proof was in flight: keep the newer lock.
    if (this.presenceSince() !== since) return false
    this.unlocked()
    return true
  }
  private unlocked() {
    this.last = this.baseline = this.now()
    this.hiddenAt = undefined
    this.set(false)
  }
  /** Wire DOM activity, visibility and a periodic check. Returns a disposer. */
  attach(target: Pick<Window, 'addEventListener' | 'removeEventListener'>, doc?: Document) {
    const onActivity = () => this.activity()
    const onVisibility = () => this.visibility(doc?.visibilityState === 'hidden')
    for (const e of ACTIVITY) target.addEventListener(e, onActivity, { passive: true })
    doc?.addEventListener('visibilitychange', onVisibility)
    if (doc?.visibilityState === 'hidden') this.visibility(true)
    this.tick()
    const timer = setInterval(() => this.tick(), TICK_MS)
    return () => {
      for (const e of ACTIVITY) target.removeEventListener(e, onActivity)
      doc?.removeEventListener('visibilitychange', onVisibility)
      clearInterval(timer)
    }
  }
}

const within = (v: unknown, lo: number, hi: number): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi
/** Per-device lock policy (a phone and a desktop want different rules). */
export async function loadLockPolicy(): Promise<LockPolicy> {
  const raw = await idb.get<Partial<LockPolicy>>(LOCK_POLICY_KEY).catch(() => undefined)
  if (
    raw &&
    (raw.desktop === 'device' || raw.desktop === 'idle') &&
    within(raw.idleMs, 5 * MIN, 24 * 60 * MIN) &&
    within(raw.backgroundMs, MIN, 8 * 60 * MIN)
  )
    return { desktop: raw.desktop, idleMs: raw.idleMs, backgroundMs: raw.backgroundMs }
  return DEFAULT_LOCK_POLICY
}
export const saveLockPolicy = (p: LockPolicy) => idb.set(LOCK_POLICY_KEY, p)

/** Phones and tablets (iPadOS reports a Mac UA but has touch points). */
export function isMobileDevice(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> | undefined = globalThis.navigator,
): boolean {
  if (!nav) return false
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(nav.userAgent)) return true
  return /Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1
}

export const persistAppLock = (record: AppLockRecord) => idb.set(APP_LOCK_KEY, record)
export const loadAppLock = () => idb.get<AppLockRecord>(APP_LOCK_KEY)

/**
 * Unlock ceremony: a fresh random challenge, user verification required, verified locally against
 * this browser's pinned passkeys when it has them (never a server-provided credential list).
 */
export function passkeyUnlock(
  passkeys: Pick<PasskeyProvider, 'assert'>,
  accountId: () => Promise<string | undefined>,
) {
  return async () => {
    const id = await accountId().catch(() => undefined)
    const trust = id ? await loadContentTrust(id).catch(() => undefined) : undefined
    const challenge = randomBytes(32)
    const assertion = await passkeys.assert(challenge, trust ? { rpId: trust.rp.rpId } : {})
    if (trust && !(await trust.verifyPasskeyApproval(assertion, challenge)).ok)
      throw new Error('Passkey not recognized on this device')
  }
}

/**
 * passkey-budget WP2: on a phone, returning to a locked Viko opens the Face ID sheet by itself
 * (once per lock, only while visible). A dismissed or refused automatic prompt is silent: the
 * Unlock button stays. Desktop never auto-prompts (it follows the OS login instead, WP1).
 */
export function shouldAutoPrompt(o: {
  locked: boolean
  mobile: boolean
  visible: boolean
  triedThisLock: boolean
}): boolean {
  return o.locked && o.mobile && o.visible && !o.triedThisLock
}
